import React from 'react';
import TextFloatingFilter from 'ui/AgGridReact/TextFloatingFilter';
import NumberFloatingFilter from 'ui/AgGridReact/NumberFloatingFilter';
import { BudgetBys, BudgetExpenseOrIncome } from 'business/constants';
import { BudgetProposalT, OperationT } from 'routes/BudgetModule/config/types';
import { ScopeT, UserScopesT } from 'business/definitions';
import { getNonEmptyFilter } from 'routes/BudgetModule/components/BudgetProposal/Resource/ExpenseIncome/Operations/helpers/operationHelpers';
import LinkCellRenderer from 'routes/BudgetModule/components/BudgetProposal/BudgetVentilated/components/LinkCellRenderer';
import { ColDef, RowGroupOpenedEvent } from 'ag-grid-community';
import {
    trackEventBudget,
    TRACKABLE_EVENTS,
    ACTIONS,
} from 'helpers/trackerHelpers';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import {
    SERVICE_PROPOSAL_AMOUNT,
    SIMULATED_ARBITRATED_AMOUNT,
    PRIMITIVE_BUDGET_AMOUNT,
    SUPPLEMENTARY_BUDGET_AMOUNT,
    OPERATION_NAME_FIELD,
} from 'routes/BudgetModule/config/constants';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import {
    ALL_OPERATION_FILE_PREFIX,
    getExcelFilename,
} from 'routes/BudgetModule/contexts/helpers';
import DetailCellRenderer from './DetailCellRenderer/DetailCellRenderer';
import AgGridReactExposingApi from '../../shared/AgGridReactExposingAPI';
import { useTranslation } from 'react-i18next';
import { useFullScreen } from 'contexts/FullScreenContext';
import { useSelectSecondaryBudgetAxisSourceTable } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableContext';
import { translateBlock } from 'i18n';
import {
    BUDGET_EXCEL_EXPORT_PARAMS,
    BUDGET_EXCEL_STYLES,
} from 'routes/BudgetModule/config/excelExportHelpers';

const StyFinancesSoftwareOperationsGrid = styled(Box, {
    name: 'StyFinancesSoftwareOperationsGrid',
    shouldForwardProp: (prop) => prop !== 'isFullScreen',
})<{ isFullScreen: boolean }>(({ isFullScreen, theme }) => ({
    width: '100%',
    height: isFullScreen ? '100%' : '40vh',
    ...(isFullScreen ? { paddingBottom: theme.spacing(2) } : {}),
    minHeight: '300px',
    '&& .ag-root-wrapper': {
        borderBottomLeftRadius: '4px',
        borderBottomRightRadius: '4px',
    },
}));

export default function Grid({
    inscriptionsByOperation,
    operationsAmounts,
    hideEmptyOperations,
    columnsOperations,
    allBudgetProposals,
    budgetBy,
    userScopes,
    scopes,
    expenseOrIncome,
}: {
    inscriptionsByOperation: InscriptionT[];
    operationsAmounts: OperationT[];
    hideEmptyOperations: boolean;
    columnsOperations: ColDef[];
    allBudgetProposals: BudgetProposalT[];
    budgetBy: BudgetBys;
    userScopes: UserScopesT;
    scopes: ScopeT[];
    expenseOrIncome: BudgetExpenseOrIncome;
}): React.ReactElement {
    const { t } = useTranslation();
    const { isFullScreen } = useFullScreen();
    const sourceTable = useSelectSecondaryBudgetAxisSourceTable();
    const nonEmptyFilter = getNonEmptyFilter([
        SERVICE_PROPOSAL_AMOUNT,
        SIMULATED_ARBITRATED_AMOUNT,
        PRIMITIVE_BUDGET_AMOUNT,
        SUPPLEMENTARY_BUDGET_AMOUNT,
    ]);

    const displayData = hideEmptyOperations
        ? operationsAmounts.filter(nonEmptyFilter)
        : operationsAmounts;

    const onRowGroupOpened = (event: RowGroupOpenedEvent) =>
        trackEventBudget(
            TRACKABLE_EVENTS.budget.budgetOverview.operations.unpackService,
            {
                action: event.expanded ? ACTIONS.open : ACTIONS.close,
                operation: event.data[OPERATION_NAME_FIELD],
            },
        );

    const colDefs: ColDef[] = columnsOperations.map((column) => {
        const propsGrid = {
            floatingFilter: true,
            flex: 1,
            resizable: true,
            ...column,
            suppressFloatingFilterButton: true,
            menuTabs: [
                'filterMenuTab' as const,
                'generalMenuTab' as const,
                'columnsMenuTab' as const,
            ],
        };
        return { key: column.field, ...propsGrid };
    });

    return (
        <StyFinancesSoftwareOperationsGrid
            className="ag-theme-material"
            isFullScreen={isFullScreen}
        >
            <AgGridReactExposingApi
                embedFullWidthRows
                masterDetail
                detailRowAutoHeight
                rowHeight={36}
                headerHeight={40}
                rowGroupPanelShow="never"
                rowData={displayData}
                context={{ inscriptionsByOperation }}
                columnDefs={colDefs}
                components={{
                    detailCellRenderer: DetailCellRenderer,
                    textFloatingFilter: TextFloatingFilter,
                    numberFloatingFilter: NumberFloatingFilter,
                    linkCellRenderer: LinkCellRenderer,
                }}
                pagination={!isFullScreen}
                paginationPageSize={10}
                detailCellRenderer="detailCellRenderer"
                detailCellRendererParams={{
                    allBudgetProposals: allBudgetProposals,
                    inscriptionsByOperation,
                    budgetBy,
                    userScopes,
                    scopes,
                }}
                onRowGroupOpened={onRowGroupOpened}
                onRowClicked={(event) => {
                    event.node.setExpanded(!event.node.expanded);
                }}
                excelExport={{
                    fileName: getExcelFilename(
                        `${ALL_OPERATION_FILE_PREFIX}_${sourceTable}`,
                        [expenseOrIncome],
                    ),
                    sheetName: t(
                        'budgetOverview.financeSecondaryBudgetAxisElement',
                        { sourceTable: translateBlock(sourceTable) },
                    ),
                }}
                excelStyles={BUDGET_EXCEL_STYLES}
                defaultExcelExportParams={BUDGET_EXCEL_EXPORT_PARAMS}
            />
        </StyFinancesSoftwareOperationsGrid>
    );
}
